import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, Film, Calendar, Users, FileText, Trash2, FolderOpen, ChevronRight, Archive, ArchiveRestore } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = () => ({ withCredentials: true });

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "" });
  const [saving, setSaving] = useState(false);
  const [crewCount, setCrewCount] = useState(0);
  const [view, setView] = useState("active"); // "active" | "archived"
  const navigate = useNavigate();

  useEffect(() => {
    fetchAll();
  }, [view]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const archivedParam = view === "archived" ? "true" : "false";
      const [pRes, cRes] = await Promise.all([
        axios.get(`${API}/projects?archived=${archivedParam}`, ax()),
        axios.get(`${API}/crew`, ax()),
      ]);
      setProjects(pRes.data);
      setCrewCount(cRes.data.length);
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const { data } = await axios.post(`${API}/projects`, form, ax());
      setProjects([data, ...projects]);
      setShowNew(false);
      setForm({ name: "" });
      navigate(`/projects/${data.id}`);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create project");
    } finally { setSaving(false); }
  };

  const deleteProject = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this project and all its call sheets? This cannot be undone. (Tip: Archive it instead to keep the data.)")) return;
    await axios.delete(`${API}/projects/${id}`, ax());
    setProjects(projects.filter((p) => p.id !== id));
  };

  const archiveProject = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Archive this project? It will be hidden from your active projects but can be restored anytime.")) return;
    try {
      await axios.post(`${API}/projects/${id}/archive`, {}, ax());
      setProjects(projects.filter((p) => p.id !== id));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to archive project");
    }
  };

  const unarchiveProject = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/projects/${id}/unarchive`, {}, ax());
      setProjects(projects.filter((p) => p.id !== id));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to restore project");
    }
  };

  const stats = [
    { label: "Total Projects", value: projects.length, icon: Film, color: "text-amber-400" },
    { label: "Crew Members", value: crewCount, icon: Users, color: "text-blue-400" },
  ];

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="text-amber-400 animate-pulse">Loading...</div>
    </div>
  );

  return (
    <div className="p-6 md:p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-amber-400/80 uppercase tracking-widest mb-1 font-mono">Dashboard</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white font-[Outfit] tracking-tight">Productions</h1>
        </div>
        <button
          data-testid="dashboard-create-project-btn"
          onClick={() => setShowNew(true)}
          className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-black font-bold px-5 py-2.5 rounded-lg transition-all duration-200 text-sm"
        >
          <Plus size={18} /> New Project
        </button>
      </div>

      {/* Active / Archived tabs */}
      <div className="flex items-center gap-1 bg-[#1A1F29] border border-[#28303F] rounded-lg p-1 w-fit">
        <button
          data-testid="dashboard-view-active-btn"
          onClick={() => setView("active")}
          className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-xs uppercase tracking-widest font-mono transition-all ${view === "active" ? "bg-amber-500/15 text-amber-400" : "text-gray-500 hover:text-gray-300"}`}
        >
          <Film size={13}/> Active
        </button>
        <button
          data-testid="dashboard-view-archived-btn"
          onClick={() => setView("archived")}
          className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-xs uppercase tracking-widest font-mono transition-all ${view === "archived" ? "bg-amber-500/15 text-amber-400" : "text-gray-500 hover:text-gray-300"}`}
        >
          <Archive size={13}/> Archived
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="quick-stats-grid">
        {stats.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-[#1A1F29] border border-[#28303F] rounded-xl p-5 hover:border-amber-500/30 transition-all duration-200">
            <Icon size={20} className={`${color} mb-3`} />
            <p className="text-3xl font-extrabold text-white font-[Outfit]">{value}</p>
            <p className="text-xs text-gray-500 mt-1">{label}</p>
          </div>
        ))}
      </div>

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#28303F] rounded-2xl" data-testid="dashboard-empty-state">
          {view === "archived" ? (
            <>
              <Archive size={40} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg font-medium">No archived projects</p>
              <p className="text-gray-600 text-sm mt-1">Archived productions live here — restore them anytime.</p>
            </>
          ) : (
            <>
              <FolderOpen size={40} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg font-medium">No productions yet</p>
              <p className="text-gray-600 text-sm mt-1">Create your first project to get started</p>
              <button onClick={() => setShowNew(true)}
                className="mt-6 bg-amber-500 hover:bg-amber-600 text-black font-bold px-6 py-2.5 rounded-lg transition-all text-sm">
                <Plus size={16} className="inline mr-2" />Create Project
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {projects.map((p) => (
            <div
              key={p.id}
              data-testid="project-card-item"
              onClick={() => navigate(`/projects/${p.id}`)}
              className={`bg-[#1A1F29] border border-[#28303F] rounded-xl p-5 hover:border-amber-500/40 cursor-pointer transition-all duration-200 group ${view === "archived" ? "opacity-80" : ""}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-2.5 rounded-lg ${view === "archived" ? "bg-gray-500/10" : "bg-amber-500/10"}`}>
                  {view === "archived"
                    ? <Archive size={20} className="text-gray-400" />
                    : <Film size={20} className="text-amber-400" />}
                </div>
                <div className="flex items-center gap-1 transition-all">
                  {view === "archived" ? (
                    <button
                      onClick={(e) => unarchiveProject(p.id, e)}
                      title="Restore from archive"
                      className="text-gray-500 hover:text-green-400 hover:bg-green-500/10 p-1.5 rounded-md transition-all"
                      data-testid="unarchive-project-btn"
                    >
                      <ArchiveRestore size={15} />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => archiveProject(p.id, e)}
                      title="Archive project"
                      className="text-gray-500 hover:text-amber-400 hover:bg-amber-500/10 p-1.5 rounded-md transition-all"
                      data-testid="archive-project-btn"
                    >
                      <Archive size={15} />
                    </button>
                  )}
                  <button
                    onClick={(e) => deleteProject(p.id, e)}
                    title="Delete permanently"
                    className="text-gray-500 hover:text-red-400 hover:bg-red-500/10 p-1.5 rounded-md transition-all"
                    data-testid="delete-project-btn"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-white font-[Outfit] mb-1">{p.name}</h3>
              {p.production_company && <p className="text-xs text-gray-500 mb-3">{p.production_company}</p>}
              <div className="space-y-1 text-xs text-gray-500">
                {p.director && <p><span className="text-gray-600">Director:</span> {p.director}</p>}
                {p.first_ad && <p><span className="text-gray-600">1st AD:</span> {p.first_ad}</p>}
              </div>
              <div className="mt-4 pt-4 border-t border-[#28303F] flex items-center justify-between">
                <span className="text-xs text-gray-600">
                  {view === "archived" && p.archived_at
                    ? `Archived ${new Date(p.archived_at).toLocaleDateString()}`
                    : new Date(p.created_at).toLocaleDateString()}
                </span>
                <ChevronRight size={16} className="text-gray-600 group-hover:text-amber-400 transition-colors" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New Project Modal */}
      {showNew && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#14181F] border border-[#28303F] rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-[#28303F]">
              <h3 className="text-lg font-bold text-white font-[Outfit]">New Production</h3>
              <button onClick={() => setShowNew(false)} className="text-gray-500 hover:text-white text-2xl leading-none">&times;</button>
            </div>
            <form onSubmit={createProject} className="p-6 space-y-4">
              <Field label="Project Title *" value={form.name} onChange={(v) => setForm({ name: v })} required />
              <p className="text-xs text-gray-500">
                You can add director, producer, departments, and more from the project dashboard once it's created.
              </p>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowNew(false)}
                  className="flex-1 border border-[#28303F] text-gray-400 hover:text-white py-2.5 rounded-lg text-sm transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={saving} data-testid="create-project-submit-btn"
                  className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold py-2.5 rounded-lg text-sm transition-all">
                  {saving ? "Creating..." : "Create Project"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text" }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">{label}</label>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2.5 text-white placeholder-gray-700 focus:outline-none focus:border-amber-500 transition-colors text-sm"
      />
    </div>
  );
}
